<template>
  <div v-if="active && items.length > 1" class="relative">
    <FadeInTransition>
      <BasicSliderArrow
        v-if="shouldShowArrows"
        side="left"
        :small="small"
        :outside="arrowsOutside"
        @click="handlePrevClick"
      />
    </FadeInTransition>
    <FadeInTransition>
      <BasicSliderArrow
        v-if="shouldShowArrows"
        side="right"
        :small="small"
        :outside="arrowsOutside"
        @click="handleNextClick"
      />
    </FadeInTransition>

    <div
      class="relative overflow-hidden"
      :class="{ 'hide-scrollbar': hideScrollbar }"
    >
      <Intersect :threshold="0.5" @enter.once="emitSlideVisible">
        <Swiper
          ref="swiperRef"
          :modules="[SwiperNavigation]"
          :slides-per-view="itemsPerSlide"
          :slides-per-group="itemsPerSlide"
          :navigation="false"
          :grab-cursor="true"
          :resistance="false"
          :allow-touch-move="true"
          :speed="600"
          :preload-images="false"
          :watch-slides-progress="true"
          :observer="true"
          :observe-parents="true"
          :center-insufficient-slides="true"
          @swiper="onSwiper"
        >
          <SwiperSlide
            v-for="(item, index) in items"
            :key="keyProp ? item[keyProp] : index"
            class="flex-none snap-center"
            :style="itemStyles"
          >
            <slot name="item" :item="item" :index="index">
              {{ item }}
            </slot>
          </SwiperSlide>
        </Swiper>
      </Intersect>
    </div>
  </div>
  <div
    v-else
    class="flex"
    :style="{
      gap: `${pixelGap}px`,
    }"
  >
    <div
      v-for="(item, index) in items"
      :key="keyProp ? item[keyProp] : index"
      :style="itemStyles"
    >
      <slot name="item" :item="item" :index="index">
        {{ item }}
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onBeforeUnmount, nextTick, computed, ref, watch } from 'vue'
import type { PropType } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation as SwiperNavigation } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper/types'
import 'swiper/css'
import { useWindowSize } from '@vueuse/core'
import { useDeviceType } from '~/composables/useDeviceType'

const props = defineProps({
  items: { type: Array as PropType<any[]>, required: true },
  keyProp: { type: String, default: undefined },
  active: { type: Boolean, default: true },
  maxItemsPerSlide: { type: Number, default: 4 },
  maxItemsPerSlideMobile: { type: Number, default: 2 },
  small: { type: Boolean, default: false },
  pixelGap: { type: Number, default: 8 },
  arrowsOutside: { type: Boolean, default: false },
  hideScrollbar: { type: Boolean, default: true },
})

const emit = defineEmits(['slideVisible'])

const { isMobile, isMd } = useDeviceType()
const swiper = ref<SwiperType>()

const itemsPerSlide = computed(() => {
  const maxItems = isMobile.value
    ? props.maxItemsPerSlideMobile
    : isMd.value
      ? props.maxItemsPerSlide - 2
      : props.maxItemsPerSlide
  return Math.min(maxItems, props.items.length)
})

const itemStyles = computed(() => ({
  width: `calc(${100 / itemsPerSlide.value}% - ${
    (props.pixelGap * (itemsPerSlide.value - 1)) / itemsPerSlide.value
  }px)`,
  flexShrink: 0,
}))

const shouldShowArrows = computed(() => {
  return props.items.length > itemsPerSlide.value
})

// activeIndex represents the position of the first item in the slide in the items array
const activeIndex = computed(() => swiper.value?.activeIndex || 0)

// items currently visible in the active slide
const visibleItems = computed(() => {
  const start = activeIndex.value
  const end = Math.min(start + itemsPerSlide.value, props.items.length)

  return props.items.slice(start, end)
})

// called only once when Swiper initializes
const onSwiper = (swiperInstance: SwiperType) => {
  swiper.value = swiperInstance
}

// used instead of handleVisibility functionality which is usually called by the intersection observer to override the emitted items and index
const emitSlideVisible = () => {
  const slideCount = Math.ceil(props.items.length / itemsPerSlide.value)
  const slideIndex =
    activeIndex.value !== 0
      ? Math.round(activeIndex.value / itemsPerSlide.value)
      : activeIndex.value
  const items = visibleItems.value.map((item, index) => ({ ...item, index: slideIndex * itemsPerSlide.value + index }))
  emit('slideVisible', items, slideCount, slideIndex)
}

const handleResize = () => {
  if (!swiper.value || swiper.value.destroyed) return
  requestAnimationFrame(() => {
    if (swiper.value && !swiper.value.destroyed) {
      swiper.value.update()
    }
  })
}

const { width } = useWindowSize()

watch(width, () => {
  nextTick(handleResize)
})

onMounted(() => {
  nextTick(handleResize)
})

onBeforeUnmount(() => {
  if (swiper.value && !swiper.value.destroyed) {
    swiper.value.destroy(true, true)
  }
})

const handlePrevClick = () => {
  if (!swiper.value || swiper.value.destroyed) return

  if (swiper.value.isBeginning) {
    const totalSlides = props.items.length
    const numGroups = Math.ceil(totalSlides / itemsPerSlide.value)
    const lastPosition = (numGroups - 1) * itemsPerSlide.value
    swiper.value.slideTo(lastPosition)
  } else {
    swiper.value.slidePrev()
  }
}

const handleNextClick = () => {
  if (!swiper.value || swiper.value.destroyed) return

  if (swiper.value.isEnd) {
    swiper.value.slideTo(0)
  } else {
    swiper.value.slideNext()
  }
}
</script>

<style lang="scss" scoped>
:deep(.swiper) {
  overflow: visible;
  width: 100%;
}

:deep(.swiper-wrapper) {
  display: flex;
  width: 100%;
}

:deep(.swiper-slide) {
  height: auto;
  flex-shrink: 0;
  padding: 2px 0;
}

.hide-scrollbar {
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
}
</style>
